"use client";

import { c as _c } from "react/compiler-runtime";
import { IconBuildings, IconListBullets } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconBoltOutline18, IconCalendarOutline18, IconClockOutline18, IconLayersOutline18, IconNodesOutline18, IconTagsOutline18, IconUserPenOutline18 } from "nucleo-ui-outline-18";
import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { EASE_OUT_QUART } from "@/lib/animation-constants";
import { cn } from "@/lib/utils";
import { HomepageFooter } from "@/components/homepage/HomepageFooter";
import WorkflowSummaryView from "@/app/(v2)/workflows/_components/WorkflowSummaryView";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { CommentsSection } from "@/components/ui/comments-section";
import { ConnectorPill } from "@/components/ui/connector-pill";
import { NodeIconTile } from "@/components/ui/node-icon-tile";
import { OrgPhoto } from "@/components/ui/OrgPhoto";
import { PageLayout } from "@/components/ui/page-layout";
import { useActions, useTriggers } from "@/hooks/useActionTriggerDefinitions";
import { useIsVip } from "@/hooks/useIsVip";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useTemplateAction } from "@/hooks/useTemplateAction";
import { FROM_ONBOARDING_QUERY_PARAM } from "@/lib/workflow/firstPublish";
import { getRequiredTemplateConnectors } from "@/lib/templates/requiredConnectors";
import { formatRelativeTime, formatUsername } from "@/lib/utils";
import { extractChainIdsFromDefinition, getTemplateIntegrationPills } from "@/lib/workflow/cardIcons";
import { extractDefinitionTokenRefs } from "@/lib/workflow/extractTokenRefs";
import type { Template } from "@/stores/templatesStore";
import { useAuth, useProfile } from "@b3dotfun/sdk/global-account/react";
import { useIsAuthenticated } from "@/hooks/useIsAuthenticated";
import { Background, ReactFlow, useNodesState, type Edge, type Node } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { motion } from "motion/react";
import { useSearchParams } from "next/navigation";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import ActionNode from "../../../(v2)/workflows/_components/ActionNode";
import { CompactViewProvider } from "../../../(v2)/workflows/_components/CompactViewContext";
import ConnectorInputModal, { computeMinConnectors } from "../../../(v2)/workflows/_components/ConnectorInputModal";
import ForEachLoopBoundary from "../../../(v2)/workflows/_components/ForEachLoopBoundary";
import LogicNode from "../../../(v2)/workflows/_components/LogicNode";
import { AutoArrangeOnMount, PreviewCanvasControls } from "../../../(v2)/workflows/_components/PreviewCanvasControls";
import TriggerNode from "../../../(v2)/workflows/_components/TriggerNode";
import WorkflowEdge from "../../../(v2)/workflows/_components/WorkflowEdge";
import { buildCompactWorkflowData, CompactWorkflowSteps, type CompactStep } from "../_components/CompactWorkflowSteps";
import { EditableTemplateCategories } from "../_components/EditableTemplateCategories";
import { EditableTemplateDescription } from "../_components/EditableTemplateDescription";
import { EditableTemplateTitle } from "../_components/EditableTemplateTitle";
import { TemplateStatsPanel } from "../_components/TemplateStatsPanel";
const nodeTypes = {
  trigger: TriggerNode,
  action: ActionNode,
  logic: LogicNode
};
const edgeTypes = {
  workflow: WorkflowEdge
};
function TemplatePreviewCanvas(t0) {
  const $ = _c(16);
  const {
    initialNodes,
    edges
  } = t0;
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const initialNodesRef = useRef(initialNodes);
  let t1;
  let t2;
  if ($[0] !== initialNodes || $[1] !== setNodes) {
    t1 = () => {
      if (initialNodesRef.current !== initialNodes) {
        initialNodesRef.current = initialNodes;
        setNodes(initialNodes);
      }
    };
    t2 = [initialNodes, setNodes];
    $[0] = initialNodes;
    $[1] = setNodes;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  const n = initialNodes.map(_temp).join("|");
  const e = edges.map(_temp2).join("|");
  t3 = `${n}#${e}`;
  const contentKey = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      padding: 0.3,
      maxZoom: 1.5,
      minZoom: 0.1
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      hideAttribution: true
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== contentKey) {
    t6 = <AutoArrangeOnMount contentKey={contentKey} />;
    $[6] = contentKey;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  let t8;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Background className="bg-ds-neutral-100 dark:bg-ds-neutral-800" gap={20} />;
    t8 = <PreviewCanvasControls />;
    t9 = <ForEachLoopBoundary disabled={true} />;
    $[8] = t7;
    $[9] = t8;
    $[10] = t9;
  } else {
    t7 = $[8];
    t8 = $[9];
    t9 = $[10];
  }
  let t10;
  if ($[11] !== edges || $[12] !== nodes || $[13] !== onNodesChange || $[14] !== t6) {
    t10 = <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} nodeTypes={nodeTypes} edgeTypes={edgeTypes} nodesDraggable={false} nodesConnectable={false} nodesFocusable={false} edgesFocusable={false} elementsSelectable={false} panOnDrag={true} zoomOnScroll={false} zoomOnPinch={true} zoomOnDoubleClick={true} preventScrolling={false} fitView={true} fitViewOptions={t4} className="dark:bg-ds-neutral-900 bg-zinc-50" proOptions={t5}>{t6}{t7}{t8}{t9}</ReactFlow>;
    $[11] = edges;
    $[12] = nodes;
    $[13] = onNodesChange;
    $[14] = t6;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}
function _temp2(edge) {
  return `${edge.source}>${edge.target}`;
}
function _temp(node) {
  return `${node.id}:${node.type ?? ""}`;
}
function PreviewTab(t0) {
  const $ = _c(7);
  const {
    active,
    onClick,
    icon,
    children
  } = t0;
  const t1 = active ? "text-ds-text-primary bg-ds-surface-card border-b-2 border-b-blue-600 dark:border-b-blue-400 dark:text-zinc-100" : "text-ds-text-muted hover:text-ds-text-primary hover:bg-ds-surface-hover border-b-2 border-b-transparent";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("border-ds-border-default flex items-center gap-2 rounded-tr-lg px-6 py-2.5 text-xs font-medium transition-colors [border-right-width:1px] last:[border-right-width:0]", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== icon || $[4] !== onClick || $[5] !== t2) {
    t3 = <button type="button" onClick={onClick} className={t2}>{icon}{children}</button>;
    $[2] = children;
    $[3] = icon;
    $[4] = onClick;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
const TRIGGER_DISPLAY_LABELS: Record<string, string> = {
  manual: "Manual",
  schedule: "Scheduled",
  "evm-log": "On-Chain Event",
  webhook: "Webhook",
  "token-price-cexes": "Price Alert",
  "erc20-receive": "Token Receive",
  "eth-receive": "Native Receive"
};
function getTriggerLabel(type: string | null): string {
  if (!type) return "Manual";
  return TRIGGER_DISPLAY_LABELS[type] || type.split("-").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}
export default function TemplateDetailClient({
  template
}: {
  template: Template;
}) {
  // The onboarding wizard appends `?fromOnboarding=1` when navigating here,
  // and we forward it onto the editor URL so the Publish stepper appears
  // post-redirect. Plain template detail visits (no query param) skip the
  // funnel entirely — only the lighter advisory pill shows in the editor.
  const searchParams = useSearchParams();
  const fromOnboarding = searchParams.get(FROM_ONBOARDING_QUERY_PARAM) === "1";
  const {
    showConnectorModal,
    setShowConnectorModal,
    isCreatingWorkflow,
    handleUseTemplate,
    handleConnectorSubmit,
    requiredTables
  } = useTemplateAction({
    fromOnboarding
  });
  const {
    isAuthenticated
  } = useAuth();
  const isAuthForLayout = useIsAuthenticated();
  const isVip = useIsVip();
  const [previewView, setPreviewView] = useState<"canvas" | "summary">("canvas");
  const editorId = template.updatedBy && template.updatedBy !== template.createdBy ? template.updatedBy : undefined;
  const {
    data: editorProfile
  } = useProfile({
    address: undefined,
    name: undefined,
    b3GlobalId: isVip && editorId ? editorId : undefined
  });
  const editorHandle = formatUsername(editorProfile?.name);
  const {
    selectedOrgId
  } = useOrganizations();
  const {
    triggers
  } = useTriggers();
  const {
    actions
  } = useActions();
  const connectorModalConfig = useMemo(() => {
    const connectorReqs = getRequiredTemplateConnectors(template);
    const minConnectors = computeMinConnectors(connectorReqs);
    return {
      connectorReqs,
      minConnectors
    };
  }, [template]);
  const descriptionTokenRefs = useMemo(() => extractDefinitionTokenRefs(template.definition), [template.definition]);
  const {
    visualNodes,
    visualEdges,
    compactData,
    nodeCount,
    triggerCount,
    actionCount,
    rootTriggerType
  } = useMemo(() => {
    const emptyResult = {
      visualNodes: [] as Node[],
      visualEdges: [] as Edge[],
      compactData: {
        triggerSteps: [] as never[],
        flowItems: [] as never[]
      },
      nodeCount: 0,
      triggerCount: 0,
      actionCount: 0,
      rootTriggerType: null as string | null
    };
    if (!template?.definition?.nodes) return emptyResult;
    const nodesObj = template.definition.nodes as Record<string, (typeof template.definition.nodes)[string] & {
      titleOverride?: string;
      description?: string;
      loopBody?: string[];
      connector?: {
        type: string;
        id?: string;
      };
    }>;
    const nodeKeys = Object.keys(nodesObj);
    if (nodeKeys.length === 0) return emptyResult;
    const flowNodes: Node[] = [];
    const flowEdges: Edge[] = [];
    type DefLite = {
      type: string;
      name?: string;
      description?: string;
      platform?: string;
      category?: string;
    };
    const triggerByType = new Map<string, DefLite>(triggers.map((t: DefLite) => [t.type, t]));
    const actionByType = new Map<string, DefLite>(actions.map((a: DefLite) => [a.type, a]));
    const childrenSet = new Set<string>();
    Object.values(nodesObj).forEach(node => {
      node.children?.forEach(childId => childrenSet.add(childId));
      node.loopBody?.forEach(childId_0 => childrenSet.add(childId_0));
    });
    const rootNodeId = nodeKeys.find(id => !childrenSet.has(id)) || nodeKeys[0];
    const positions = new Map<string, {
      x: number;
      y: number;
    }>();
    let yOffset = 0;
    const calcPositions = (nodeId: string) => {
      if (positions.has(nodeId)) return;
      positions.set(nodeId, {
        x: 0,
        y: yOffset
      });
      yOffset += 180;
      nodesObj[nodeId]?.children?.forEach(childId_1 => calcPositions(childId_1));
      nodesObj[nodeId]?.loopBody?.forEach(childId_2 => calcPositions(childId_2));
    };
    calcPositions(rootNodeId);
    const compactDataValue = buildCompactWorkflowData(template.definition, triggers, actions);
    let triggerCountLocal = 0;
    let actionCountLocal = 0;
    Object.entries(nodesObj).forEach(([nodeId_0, node_0]) => {
      const isTrigger = triggerByType.has(node_0.type);
      const isAction = actionByType.has(node_0.type);
      if (isTrigger) triggerCountLocal++;
      if (isAction) actionCountLocal++;
      const definition = isTrigger ? triggerByType.get(node_0.type) : actionByType.get(node_0.type);
      const position = positions.get(nodeId_0) || {
        x: 250,
        y: 0
      };
      const isLogic = !isTrigger && (definition as {
        category?: string;
      })?.category === "logic";
      flowNodes.push({
        id: nodeId_0,
        type: isTrigger ? "trigger" : isLogic ? "logic" : "action",
        position,
        data: {
          name: node_0.titleOverride || definition?.name || nodeId_0,
          type: node_0.type,
          description: node_0.description || definition?.description || "",
          platform: definition?.platform || node_0.type,
          payload: node_0.payload || {},
          connector: node_0.connector || undefined,
          isTemplateView: true
        },
        draggable: false,
        selectable: false,
        focusable: false
      });
      const isForEach = node_0.type === "for-each";
      node_0.children?.forEach((childId_3: string) => {
        flowEdges.push({
          id: `${nodeId_0}=>${childId_3}`,
          source: nodeId_0,
          target: childId_3,
          type: "workflow",
          animated: false,
          data: {
            hideAddButton: true,
            ...(isForEach && {
              isOnComplete: true
            })
          }
        });
      });
      node_0.loopBody?.forEach((lbId: string) => {
        flowEdges.push({
          id: `${nodeId_0}=>${lbId}`,
          source: nodeId_0,
          target: lbId,
          type: "workflow",
          animated: false,
          data: {
            hideAddButton: true
          }
        });
      });
    });
    return {
      visualNodes: flowNodes,
      visualEdges: flowEdges,
      compactData: compactDataValue,
      nodeCount: Object.keys(nodesObj).length,
      triggerCount: triggerCountLocal,
      actionCount: actionCountLocal,
      rootTriggerType: nodesObj[rootNodeId]?.type || null
    };
  }, [template, triggers, actions]);
  const uncappedFlowSteps = useMemo(() => {
    const allData = buildCompactWorkflowData(template.definition, triggers, actions, {
      maxFlowSteps: 999,
      showOverflowRow: false
    });
    return allData.flowItems.filter((item): item is CompactStep => item.kind === "step");
  }, [template.definition, triggers, actions]);
  const integrationChainIds = useMemo(() => extractChainIdsFromDefinition(template?.definition), [template?.definition]);
  const {
    data: chainInfoMap
  } = useChainInfoMap(integrationChainIds);
  const integrationPills = useMemo(() => getTemplateIntegrationPills(template?.definition, triggers, actions, chainInfoMap), [template?.definition, triggers, actions, chainInfoMap]);
  const categoryTags = useMemo(() => template?.tags?.filter(tag => !tag.imageUrl) || [], [template?.tags]);
  const breadcrumb = <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink href="/templates">Template Library</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{template.name}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>;
  const headerCard = <motion.div initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.6,
    ease: EASE_OUT_QUART
  }} className="rounded-ds-2xl border-ds-border bg-ds-surface-card shadow-ds-soft relative flex flex-col items-stretch gap-6 border p-2.5 lg:flex-row lg:items-center lg:py-2.5 lg:pl-8 lg:pr-2.5">
      <div className="flex flex-1 flex-col gap-[42px] px-[22px] pt-[22px] lg:p-0">
        <div className="flex flex-col gap-5">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: EASE_OUT_QUART
        }}>
            <NodeIconTile size="lg" id={template.id} subject={template.name} context={template.description || ""} template="b3os-app-flat" alt={template.name} />
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.18,
          ease: EASE_OUT_QUART
        }}>
            <EditableTemplateTitle template={template} canEdit={isAuthenticated && !!template.organizationId && selectedOrgId === template.organizationId} />
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.26,
        ease: EASE_OUT_QUART
      }} className="flex items-center gap-1.5">
          <IconUserPenOutline18 className="text-content-secondary h-5 w-5" />
          <span className="text-content-secondary text-base leading-6">Created by</span>
          <OrgPhoto src={template.organizationPhoto} alt={template.organizationName || ""} size={24} rounded="full" className="border border-black/25" fallback={<div className="bg-ds-surface-muted flex h-6 w-6 items-center justify-center rounded-full">
                <IconBuildings className="text-ds-text-muted h-3 w-3" />
              </div>} />
          <span className="text-ds-text-tertiary text-base font-medium leading-6">
            {template.organizationName || template.createdBy || "B3 Team"}
            {isVip && editorId && editorHandle ? ` (${editorHandle})` : null}
          </span>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.34,
        ease: EASE_OUT_QUART
      }}>
          <button type="button" onClick={() => handleUseTemplate(template)} disabled={isCreatingWorkflow} className="rounded-ds-md shadow-ds-brand relative flex h-[44px] w-[206px] items-center justify-center gap-1 overflow-hidden px-6 py-2.5 text-sm font-medium text-white transition-transform hover:scale-[1.02] disabled:opacity-50">
            <span className="rounded-ds-md from-ds-brand-500 to-ds-brand-600 pointer-events-none absolute inset-0 bg-gradient-to-b" />
            <span className="relative">Use this template</span>
            <span className="rounded-inherit shadow-ds-inner-light pointer-events-none absolute inset-0" />
          </button>
        </motion.div>
      </div>

      <CompactWorkflowSteps data={compactData} className="min-h-[360px] flex-1 justify-center" animate cycleSteps={uncappedFlowSteps} />
    </motion.div>;
  const mainContent = <div className="flex gap-10">
      <div className="min-w-0 flex-1">
        <EditableTemplateDescription template={template} canEdit={isAuthenticated && !!template.organizationId && selectedOrgId === template.organizationId} tokenRefs={descriptionTokenRefs} />

        {visualNodes.length > 0 && <section className="mb-10">
            <h2 className="text-ds-text-primary mb-3 text-base font-semibold">Preview</h2>
            <div className="border-ds-border bg-ds-surface-card group relative flex flex-col overflow-hidden rounded-2xl border">
              <div className="border-ds-border-default bg-ds-surface-sunken flex items-center border-b">
                <PreviewTab active={previewView === "canvas"} onClick={() => setPreviewView("canvas")} icon={<IconNodesOutline18 className="h-3 w-3" />}>
                  Canvas
                </PreviewTab>
                <PreviewTab active={previewView === "summary"} onClick={() => setPreviewView("summary")} icon={<IconListBullets className="h-3 w-3" />}>
                  Summary
                </PreviewTab>
              </div>
              <div className="relative" style={{
            aspectRatio: "135 / 90"
          }}>
                {previewView === "canvas" ? <CompactViewProvider>
                    <TemplatePreviewCanvas initialNodes={visualNodes} edges={visualEdges} />
                  </CompactViewProvider> : <div className="absolute inset-0 [&>div]:!max-h-full [&>div]:!bg-transparent">
                    <WorkflowSummaryView nodes={visualNodes} edges={visualEdges} workflowName={template.name} workflowDescription={template.description || undefined} publicReadOnly />
                  </div>}
              </div>
            </div>
          </section>}

        <section>
          <CommentsSection roomId={`template:${template.id}`} title="Discussion" variant="minimal" />
        </section>
      </div>

      <aside className="hidden w-[220px] shrink-0 lg:block">
        <div className="sticky top-4 space-y-8">
          <div>
            <h3 className="text-ds-text-primary mb-4 text-sm font-medium">Template details:</h3>
            <ul className="text-ds-text-primary space-y-3 text-sm leading-5">
              <li className="flex items-center gap-2">
                <IconLayersOutline18 className="text-ds-text-disabled h-4 w-4 shrink-0" />
                <span>
                  <span className="font-medium">
                    {nodeCount} Node{nodeCount !== 1 ? "s" : ""}
                  </span>{" "}
                  <span className="text-ds-text-muted">
                    ({triggerCount} trigger{triggerCount !== 1 ? "s" : ""}, {actionCount} action
                    {actionCount !== 1 ? "s" : ""})
                  </span>
                </span>
              </li>

              <li className="flex items-center gap-2">
                <IconBoltOutline18 className="text-ds-text-disabled h-4 w-4 shrink-0" />
                <span>
                  Trigger: <span className="font-medium">{getTriggerLabel(rootTriggerType)}</span>
                </span>
              </li>

              {template.createdAt && <li className="flex items-center gap-2">
                  <IconCalendarOutline18 className="text-ds-text-disabled h-4 w-4 shrink-0" />
                  <span>
                    Created <span className="font-medium">{formatRelativeTime(new Date(template.createdAt))}</span>
                  </span>
                </li>}

              {template.updatedAt && template.updatedAt !== template.createdAt && <li className="flex items-center gap-2">
                  <IconClockOutline18 className="text-ds-text-disabled h-4 w-4 shrink-0" />
                  <span>
                    Updated <span className="font-medium">{formatRelativeTime(new Date(template.updatedAt))}</span>
                  </span>
                </li>}

              {(categoryTags.length > 0 || isAuthenticated && !!template.organizationId && selectedOrgId === template.organizationId) && <li className="flex items-start gap-2">
                  <IconTagsOutline18 className="text-ds-text-disabled mt-0.5 h-4 w-4 shrink-0" />
                  <EditableTemplateCategories template={template} canEdit={isAuthenticated && !!template.organizationId && selectedOrgId === template.organizationId} />
                </li>}
            </ul>
          </div>

          {integrationPills.length > 0 && <div>
              <h3 className="text-ds-text-primary mb-3 text-sm font-medium">Integrate with:</h3>
              <div className="flex flex-wrap gap-2">
                {integrationPills.map(pill => <ConnectorPill key={pill.key} connectorType={pill.connectorType ?? pill.label} label={pill.label} logoUrl={pill.logoUrl} />)}
              </div>
            </div>}

          <TemplateStatsPanel templateId={template.id} />
        </div>
      </aside>
    </div>;
  const modal = <ConnectorInputModal open={showConnectorModal} onOpenChange={setShowConnectorModal} requiredConnectors={connectorModalConfig.connectorReqs} onSubmit={handleConnectorSubmit} isSubmitting={isCreatingWorkflow} title={template.templateProps?.length ? "Configure Template" : "Configure Template Connectors"} description={template.templateProps?.length ? template.name || "Configure this template" : "This template requires connectors to work properly."} templateProps={template.templateProps} minConnectors={connectorModalConfig.minConnectors} definition={template.definition} requiredTables={requiredTables} tableSchemas={template.tableSchemas} />;
  if (isAuthForLayout) {
    return <PageLayout>
        <div className="flex flex-col gap-6">
          {breadcrumb}
          {headerCard}
          {mainContent}
        </div>
        {modal}
      </PageLayout>;
  }
  return <div className="bg-background flex min-h-screen flex-col">
      <div className="mx-auto flex w-full max-w-[1240px] flex-col gap-[37px] px-4 pb-[100px] pt-[120px]">
        {breadcrumb}
        {headerCard}
        {mainContent}
      </div>

      <HomepageFooter hideGrids />
      {modal}
    </div>;
}